import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@/lib/theme/useTheme';
import { useUser } from '@/lib/auth/useUser';
import { ToggleGroup, ToggleGroupItem } from '@/components/molecules/ToggleGroup';
import { SettingRow } from '@/components/molecules/SettingRow';
import { apiClient } from '@/lib/http/apiClient';
import { AuthenticatedShell } from '@/features/app/AuthenticatedShell';

export const Route = createFileRoute('/settings/profile')({ component: ProfilePage });

function ProfilePage() {
  const me = useUser();
  const { preference, setPreference } = useTheme();
  const queryClient = useQueryClient();

  const persistTheme = useMutation({
    mutationFn: async (t: string) => (await apiClient.patch('/api/v1/me/preferences', { theme: t })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'] }),
  });

  const handleThemeChange = (value: string) => {
    if (!value) return;
    const t = value as 'system' | 'light' | 'dark' | 'high-contrast';
    setPreference(t);
    if (me.data) persistTheme.mutate(t);
  };

  return (
    <AuthenticatedShell activeNavId="settings">
      <div className="mx-auto max-w-3xl px-8 py-10">
        <h1 className="font-display text-display tracking-tight text-foreground">
          Profile &amp; <span className="italic text-primary">preferences</span>.
        </h1>
        <p className="mt-3 text-body-sm text-muted-foreground">Customize your appearance and account.</p>
        <div className="mt-8 rounded-lg border border-border bg-card px-6">
          <SettingRow
            label="Theme"
            description="Light, Dark, or High contrast (AAA). System follows your OS preference."
            control={
              <ToggleGroup type="single" value={preference} onValueChange={handleThemeChange} aria-label="Theme">
                <ToggleGroupItem value="system">System</ToggleGroupItem>
                <ToggleGroupItem value="light">Light</ToggleGroupItem>
                <ToggleGroupItem value="dark">Dark</ToggleGroupItem>
                <ToggleGroupItem value="high-contrast">High contrast</ToggleGroupItem>
              </ToggleGroup>
            }
          />
          {me.data ? (
            <SettingRow
              label="Email"
              description={me.data.user.email}
              control={<span className="text-caption text-muted-foreground">Managed by Auth0</span>}
            />
          ) : null}
        </div>
      </div>
    </AuthenticatedShell>
  );
}
